// biome-ignore-all lint/a11y: TODO - address a11y issues later
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Info,
  Search,
} from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { CSVExportButton } from "@/components/csv-export-button";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import { Button } from "@/components/ui/button";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAccountActivities } from "@/hooks/api/use-account-activities";
import {
  type Person,
  type PersonFragment,
  useAccountPersons,
} from "@/hooks/api/use-account-persons";
import { useAccountsByIds } from "@/hooks/api/use-accounts";
import {
  type ExternalConnectionLinks,
  useExternalConnectionLinks,
} from "@/hooks/api/use-external-connection-links";
import { useTableCSVExport } from "@/hooks/use-csv-export";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";

interface PersonFragmentsTableProps {
  fragments: PersonFragment[];
  connections: ExternalConnectionLinks[];
}

function PersonFragmentsTable({
  fragments,
  connections,
}: PersonFragmentsTableProps) {
  return (
    <div className="px-8 py-2">
      <div className="border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border bg-muted/50">
              <TableHead className="h-9 text-xs">Source</TableHead>
              <TableHead className="h-9 text-xs text-left w-[160px]">
                Name
              </TableHead>
              <TableHead className="h-9 text-xs">Email</TableHead>
              <TableHead className="h-9 text-xs">Title</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fragments.map((fragment) => (
              <TableRow
                key={fragment.id}
                className="hover:bg-muted/50 border-b border-border/50 last:border-0"
              >
                <TableCell className="py-2">
                  {fragment.identifier_type === "external_record_id" ? (
                    fragment.identifier_external_record_type && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {(() => {
                            const url = getExternalConnectionLink(
                              {
                                source_record_id: fragment.external_identifier,
                                source_record_type:
                                  fragment.identifier_external_record_type,
                              },
                              connections,
                            );

                            if (url) {
                              return (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary flex items-center gap-1"
                                >
                                  {fragment.identifier_external_record_type}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              );
                            } else {
                              return (
                                <span className="text-xs text-muted-foreground">
                                  {fragment.identifier_external_record_type}
                                </span>
                              );
                            }
                          })()}
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {getExternalConnectionLink(
                              {
                                source_record_id: fragment.external_identifier,
                                source_record_type:
                                  fragment.identifier_external_record_type,
                              },
                              connections,
                            )
                              ? `View in ${getExternalSystemName(
                                  fragment.identifier_external_record_type,
                                )}`
                              : `External record from ${getExternalSystemName(
                                  fragment.identifier_external_record_type,
                                )}`}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )
                  ) : fragment.identifier_type === "email_address" ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="text-xs text-muted-foreground">
                          Discovered in touchpoints
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          This person was discovered through email interactions
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell className="py-2">
                  <span className="text-sm text-muted-foreground">
                    {fragment.first_name} {fragment.last_name}
                  </span>
                </TableCell>
                <TableCell className="py-2">
                  {fragment.email_address && (
                    <a
                      href={`mailto:${fragment.email_address}`}
                      className="text-sm text-primary"
                    >
                      {fragment.email_address}
                    </a>
                  )}
                </TableCell>
                <TableCell className="py-2">
                  <span className="text-sm text-muted-foreground">
                    {fragment.job_title}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

interface AccountPersonsProps {
  accountId?: string;
  onPersonClick?: (personId: string) => void;
}

type SortField = "name" | "email" | "title" | "engagements" | "bids";

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

// Define export columns for people
const personExportColumns: UnifiedTableColumn<
  Person & { inboundTouchpoints: number; outboundTouchpoints: number }
>[] = [
  {
    key: "first_name",
    title: "First Name",
  },
  {
    key: "last_name",
    title: "Last Name",
  },
  {
    key: "email_address",
    title: "Email",
  },
  {
    key: "job_title",
    title: "Title",
  },
  {
    key: "inboundTouchpoints",
    title: "Engagements",
  },
  {
    key: "outboundTouchpoints",
    title: "Bids",
  },
];

export function AccountPersons({
  accountId,
  onPersonClick,
}: AccountPersonsProps) {
  const { persons, isLoading: isLoadingPersons } = useAccountPersons(accountId);
  const { data: activities, isLoading: isLoadingActivities } =
    useAccountActivities(accountId);
  const { data: connections } = useExternalConnectionLinks();
  const { data: accounts } = useAccountsByIds(
    accountId ? [accountId] : undefined,
  );
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: "engagements",
    direction: "desc",
  });

  const handlePersonClick = (personId: string) => {
    if (onPersonClick) {
      onPersonClick(personId);
    }
  };

  const personsWithTouchpoints = useMemo(() => {
    if (!persons || !activities) return [];

    const touchpointCounts = activities.reduce(
      (counts, activity) => {
        if (activity.people) {
          activity.people.forEach((person) => {
            if (activity.direction === "IN") {
              counts.inbound[person.id] = (counts.inbound[person.id] || 0) + 1;
            } else if (activity.direction === "OUT") {
              counts.outbound[person.id] =
                (counts.outbound[person.id] || 0) + 1;
            }
          });
        }
        return counts;
      },
      { inbound: {}, outbound: {} } as {
        inbound: Record<string, number>;
        outbound: Record<string, number>;
      },
    );

    const enrichedPersons = (persons as Person[]).map((person) => ({
      ...person,
      inboundTouchpoints: touchpointCounts.inbound[person.id] || 0,
      outboundTouchpoints: touchpointCounts.outbound[person.id] || 0,
    }));

    return [...enrichedPersons].sort((a, b) => {
      const { field, direction } = sortConfig;
      const multiplier = direction === "asc" ? 1 : -1;

      switch (field) {
        case "name":
          return (
            multiplier *
            `${a.first_name} ${a.last_name}`.localeCompare(
              `${b.first_name} ${b.last_name}`,
            )
          );
        case "email":
          return (
            multiplier *
            (a.email_address || "").localeCompare(b.email_address || "")
          );
        case "title":
          return (
            multiplier * (a.job_title || "").localeCompare(b.job_title || "")
          );
        case "engagements":
          return multiplier * (a.inboundTouchpoints - b.inboundTouchpoints);
        case "bids":
          return multiplier * (a.outboundTouchpoints - b.outboundTouchpoints);
        default:
          return 0;
      }
    });
  }, [persons, activities, sortConfig]);

  // Set up CSV export
  const { isExporting, exportData, defaultFilename } = useTableCSVExport({
    data: personsWithTouchpoints,
    columns: personExportColumns,
    filename: `${
      accounts?.results?.find((a) => a.id === accountId)?.name ||
      `account-${accountId}`
    }_people_${new Date().toISOString().split("T")[0]}`,
  });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  const toggleSort = (field: SortField) => {
    setSortConfig((current) => ({
      field,
      direction:
        current.field === field
          ? current.direction === "asc"
            ? "desc"
            : "asc"
          : "desc",
    }));
  };

  const SortableHeader = ({
    field,
    children,
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => (
    <Button
      variant="ghost"
      className="h-8 px-2 hover:bg-muted flex items-center gap-2"
      onClick={() => toggleSort(field)}
    >
      {children}
      {sortConfig.field === field &&
        (sortConfig.direction === "desc" ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronUp className="h-4 w-4" />
        ))}
    </Button>
  );

  const toggleRow = (personId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [personId]: !prev[personId],
    }));
  };

  if (!accountId) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold">People</h2>

        {/* CSV Export Button */}
        {!isLoadingPersons &&
          !isLoadingActivities &&
          personsWithTouchpoints.length > 0 && (
            <ExportFilenameDialog
              open={isExportDialogOpen}
              onOpenChange={setIsExportDialogOpen}
              defaultFilename={defaultFilename}
              extension="csv"
              onExport={handleExport}
              disabled={isExporting}
            >
              <CSVExportButton
                onClick={() => setIsExportDialogOpen(true)}
                isExporting={isExporting}
                disabled={isExporting}
                tooltipText="Export people to CSV"
              />
            </ExportFilenameDialog>
          )}
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-8"></TableHead>
            <TableHead className="text-left w-[160px]">
              <SortableHeader field="name">Person</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="email">Email</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="title">Title</SortableHeader>
            </TableHead>
            <TableHead>
              <div className="flex items-center gap-2">
                <SortableHeader field="engagements">Engagements</SortableHeader>
                <InfoTooltip
                  trigger={
                    <Info className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer" />
                  }
                  side="top"
                  align="start"
                  className="max-w-[200px]"
                >
                  Count of engagement (incoming) touchpoints initiated by this
                  person (i.e., emails sent to a salesperson, but not emails
                  received from a salesperson).
                </InfoTooltip>
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center gap-2">
                <SortableHeader field="bids">Bids</SortableHeader>
                <InfoTooltip
                  trigger={
                    <Info className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer" />
                  }
                  side="top"
                  align="start"
                  className="max-w-[200px]"
                >
                  Count of bid (outgoing) touchpoints targeted to this person
                  (i.e., emails sent by a salesperson, but not replies sent by
                  this person)
                </InfoTooltip>
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoadingPersons || isLoadingActivities ? (
            <TableRow>
              <TableCell colSpan={6}>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </TableCell>
            </TableRow>
          ) : personsWithTouchpoints.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="h-24 text-center text-muted-foreground"
              >
                No people found
              </TableCell>
            </TableRow>
          ) : (
            personsWithTouchpoints.map((person) => (
              <Fragment key={person.id}>
                <TableRow className="group">
                  <TableCell className="w-8">
                    <button
                      onClick={() => toggleRow(person.id)}
                      className="p-1 hover:bg-accent rounded-sm"
                    >
                      {expandedRows[person.id] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-start gap-2 w-full">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handlePersonClick(person.id)}
                            className="group/name gap-2 flex items-start hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors text-left"
                          >
                            <span className="text-left">
                              {person.first_name} {person.last_name}
                            </span>
                            <Search className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover/name:opacity-100 flex-shrink-0 mt-0.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Highlight touchpoints with this person</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell>
                    {person.email_address && (
                      <a
                        href={`mailto:${person.email_address}`}
                        className="text-primary"
                      >
                        {person.email_address}
                      </a>
                    )}
                  </TableCell>
                  <TableCell>{person.job_title}</TableCell>
                  <TableCell>{person.inboundTouchpoints}</TableCell>
                  <TableCell>{person.outboundTouchpoints}</TableCell>
                </TableRow>
                {expandedRows[person.id] && (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0 hover:bg-background">
                      <PersonFragmentsTable
                        fragments={person.fragments}
                        connections={connections ?? []}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
